import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TablePagination
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ExportHistoryTable } from '../components/modules/export';
import { getAllDdqDocuments, exportDdqDocument, getExportHistory } from '../services/api';

interface DdqDocument {
  id: string;
  name: string;
  // Add other relevant document properties here
}

interface ExportHistoryItem {
  document_id: string;
  ddq_name: string;
  format: string;
  status: string;
  file_path: string;
  id: string;
  exported_by_id: string;
  organization_id: string;
  created_at: string;
}

const Export: React.FC = () => {
  const navigate = useNavigate();
  const [openDocumentDialog, setOpenDocumentDialog] = useState(false);
  const [ddqDocuments, setDdqDocuments] = useState<DdqDocument[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<DdqDocument | null>(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [exportFormat, setExportFormat] = useState('docx');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportHistory, setExportHistory] = useState<ExportHistoryItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [totalHistory, setTotalHistory] = useState(0);
  
  const [tabValue, setTabValue] = useState(0);
  
  useEffect(() => {
    if (tabValue === 1) {
      fetchHistory(historyPage, historyRowsPerPage);
    }
  }, [tabValue, historyPage, historyRowsPerPage]);

  const fetchHistory = async (page: number, size: number) => {
    setIsLoadingHistory(true);
    try {
      const response = await getExportHistory({ page: page + 1, size });
      setExportHistory(response.history || []);
      setTotalHistory(response.total || 0);
    } catch (error) {
      console.error('Failed to fetch export history:', error);
      setSnackbarMessage('Failed to fetch export history.');
      setSnackbarOpen(true);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleOpenDocumentDialog = async (format: 'docx' | 'xlsx' | 'pdf') => {
    setExportFormat(format);
    setIsLoadingDocuments(true);
    setOpenDocumentDialog(true);
    try {
      const response = await getAllDdqDocuments();
      setDdqDocuments(response.documents || []);
    } catch (error) {
      console.error('Failed to fetch DDQ documents:', error);
      setSnackbarMessage('Failed to fetch DDQ documents.');
      setSnackbarOpen(true);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleCloseDocumentDialog = () => {
    setOpenDocumentDialog(false);
    setSelectedDocument(null);
  };

  const handleSelectDocument = (doc: DdqDocument) => {
    setSelectedDocument(doc);
  };

  const handleExport = async () => {
    if (!selectedDocument) {
      setSnackbarMessage('Please select a document to export.');
      setSnackbarOpen(true);
      return;
    }

    setIsExporting(true);
    
    try {
      const result = await exportDdqDocument(selectedDocument.id, exportFormat as 'docx' | 'xlsx' | 'pdf');
      
      if (result.success) {
        // Refresh export history
        fetchHistory(0, historyRowsPerPage);
        
        // Close the dialog
        setOpenDocumentDialog(false);
        
        // Show success message
        setSnackbarMessage(result.message);
        setSnackbarOpen(true);
      } else {
        // Show error message
        setSnackbarMessage(`Export failed: ${result.message}`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Export error:', error);
      setSnackbarMessage('An error occurred during export. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/ddq')} sx={{ mr: 1 }}>
                <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">
              Export DDQ
            </Typography>
        </Box>
        {/* <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
        >
          Export DDQ
        </Button> */}
      </Box>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="export tabs">
            <Tab label="Export Options" />
            <Tab label="Export History" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>
        
        {tabValue === 0 && (
          <Box>
            <Typography variant="body1" paragraph>
              Generate and export your completed DDQ responses in various formats to meet LP requirements.
            </Typography>
            
            <Box sx={{ mt: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Word Document
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                      Export as a formatted Word document with tables and sections.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => {
                        handleOpenDocumentDialog('docx');
                      }}
                      startIcon={<DownloadIcon />}
                    >
                      Export as Word
                    </Button>
                  </Paper>
                </Box>
                
                <Box sx={{ flex: '1 1 300px' }}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Excel Spreadsheet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                      Export as an Excel spreadsheet with questions and answers in rows.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => {
                        handleOpenDocumentDialog('xlsx');
                      }}
                      startIcon={<DownloadIcon />}
                    >
                      Export as Excel
                    </Button>
                  </Paper>
                </Box>
                
                <Box sx={{ flex: '1 1 300px' }}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      PDF Document
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                      Export as a read-only PDF document with preserved formatting.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => {
                        handleOpenDocumentDialog('pdf');
                      }}
                      startIcon={<DownloadIcon />}
                    >
                      Export as PDF
                    </Button>
                  </Paper>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ mt: 4 }}>
              {/* <Typography variant="h6" gutterBottom>
                Current DDQ Status
              </Typography>
               */}
              {/*
                Export Summary Section
                Displays a summary of the DDQ's status, including the total number of questions,
                how many are answered, unanswered, and pending approval.
                This provides a quick overview for the user before exporting.
              */}
              {/* <ExportSummary
                totalQuestions={mockDDQData.totalQuestions}
                answeredQuestions={mockDDQData.answeredQuestions}
                unansweredQuestions={mockDDQData.unansweredQuestions}
                pendingApproval={mockDDQData.pendingApproval}
              /> */}
            </Box>
          </Box>
        )}
        
        {tabValue === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Export History
            </Typography>
            
            <Typography variant="body2" paragraph>
              View and download previous exports of your DDQ responses.
            </Typography>
            
            {isLoadingHistory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ExportHistoryTable history={exportHistory} />
            )}
            <TablePagination
              component="div"
              count={totalHistory}
              page={historyPage}
              onPageChange={(e, newPage) => setHistoryPage(newPage)}
              rowsPerPage={historyRowsPerPage}
              onRowsPerPageChange={(e) => {
                setHistoryRowsPerPage(parseInt(e.target.value, 10));
                setHistoryPage(0);
              }}
            />
          </Box>
        )}
      </Paper>
      
      {/* Document Selection Dialog */}
      <Dialog open={openDocumentDialog} onClose={handleCloseDocumentDialog} fullWidth maxWidth="sm">
        <DialogTitle>Select DDQ Document</DialogTitle>
        <DialogContent dividers>
          {isLoadingDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {ddqDocuments.map((doc) => (
                <ListItem
                  button
                  key={doc.id}
                  selected={selectedDocument?.id === doc.id}
                  onClick={() => handleSelectDocument(doc)}
                >
                  <ListItemText primary={doc.name} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDocumentDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            disabled={!selectedDocument || isExporting}
          >
            {isExporting ? <CircularProgress size={24} /> : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default Export;